import { Component, forwardRef, Input, OnChanges, OnInit, Optional, SimpleChanges } from '@angular/core';
import { ControlContainer, NG_VALUE_ACCESSOR } from "@angular/forms";
import { BaseControlComponentDirective } from "../base-control-component.directive";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, combineLatest, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import {
    WorkflowDropdownItem,
    WorkflowDropdownItemEntity,
    WorkflowDropdownValue
} from "../../models/dropdowns/workflow-dropdown-item";
import { serviceDropdownEntity, workflowDropdownEntity } from "../../store/reducers";
import { Store } from "@ngrx/store";
import { State } from "../../store";
import { PredefinedDropdownGroupService } from "../../services/predefined/predefined-dropdown-group.service";
import { RefDataService } from '../../services/general/refData.service';
import { DropDownItem } from '../../models/general/dropdownItem.model';
import { ROBERT_OPTION_STYLE, ROBERT_OPTION_STYLE_DISABLED } from 'app/main/pm/projects/shared/robert-logo';
import { RobertWorkflowAvailability, robertStepName } from 'app/services/robert/robert.service';

@Component({
    selector: 'tp-workflow-dropdown',
    templateUrl: './workflow-dropdown.component.html',
    styleUrls: ['./workflow-dropdown.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => WorkflowDropdownComponent),
            multi: true,
        },
    ],
})
export class WorkflowDropdownComponent extends BaseControlComponentDirective implements OnInit, OnChanges {
    @Input() set value(value: WorkflowDropdownValue) {
        this.workflowValue$.next(value);
    }

    @Input() selectedServiceId: number;
    // Service ids whose steps go to Robert (Service.UseInRobertIntegration) — matching step options get a small
    // Robert logo marker. Left unbound (null) by hosts that don't care (e.g. quote-job) → no markers.
    @Input() robertServiceIds: number[] | null = null;
    // Robert workflow templates + per-step TP coverage (GET /robert/workflows) — renders the "Robert workflows"
    // section. Fully-covered rows are presets (click selects their mapped TP steps); rows with unmapped steps
    // render grayed/unclickable. Left unbound (null) by hosts that don't care → section absent.
    @Input() robertWorkflows: RobertWorkflowAvailability[] | null = null;
    private robertWorkflowItems$ = new BehaviorSubject<WorkflowDropdownItem[]>([]);
    workflowValue$ = new BehaviorSubject<WorkflowDropdownValue>({ workflows: [], workflowSteps: [] });
    workflowOptions$: Observable<WorkflowDropdownItem[]>;
    workflowDropdownItems: DropDownItem[] = []; // Local storage for dropdown items
    serviceDropdownItems: DropDownItem[] = []; // Local storage for dropdown items
    private mappedItems: WorkflowDropdownItem[] = []; // The currently rendered option objects (for in-place marker updates)

    dropdownGroupByWorkflow = this.predefinedDropdownGroupService.dropdownGroupByWorkflow();

    constructor(
        @Optional() protected readonly controlContainer: ControlContainer,
        protected readonly translateService: TranslateService,
        private refDataService: RefDataService,
        private store: Store<State>,
        private predefinedDropdownGroupService: PredefinedDropdownGroupService
    ) {
        super(controlContainer, translateService);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.robertServiceIds) {
            // Markers are applied by MUTATING the rendered option objects — re-creating them would orphan the
            // control's current selections (tp-dropdown compares options by the per-mapping random id).
            this.applyRobertMarkers();
        }
        if (changes.robertWorkflows) {
            // Safe to re-create THESE rows: they are presets that never stay selected, so nothing gets orphaned.
            this.robertWorkflowItems$.next(this.buildRobertWorkflowItems());
        }
        if (Object.keys(changes).every(key => key === 'robertServiceIds' || key === 'robertWorkflows')) return;
        if (changes.selectedServiceId && this.selectedServiceId != null && this.selectedServiceId !== 0) {
            this.loadDataFromServer();
        } else {
            this.initializeWorkflowOptions();
        }
    }

    ngOnInit() {
        if (this.selectedServiceId != null && this.selectedServiceId !== 0) {
            this.loadDataFromServer();
        } else {
            this.initializeWorkflowOptions();
        }
    }

    loadDataFromServer() {
        this.refDataService.getDropdownServicesWorkflowStepsByServiceId(this.selectedServiceId).subscribe(data => {
            this.workflowDropdownItems = data.response.filter(item => item.useAsDefaultFilter === true);
            this.serviceDropdownItems = data.response.filter(item => item.useAsDefaultFilter === false);
            this.initializeWorkflowOptions(); // After loading data, initialize options
        });
    }

    initializeWorkflowOptions() {
        const workflowItems = this.workflowDropdownItems.map(item => this.mapToWorkflowItem(item));
        const serviceStepItems = this.serviceDropdownItems.map(item => this.mapToWorkflowItem(item));
        this.mappedItems = [...workflowItems, ...serviceStepItems];
        this.applyRobertMarkers();

        const workflows$ = of(workflowItems);

        const serviceSteps$ = of(serviceStepItems);

        // This map replays the host-set `value` input over the option objects (setValue below). The Robert
        // rows are merged AFTER it, in a separate combineLatest: /robert/workflows is a slow live call, and if
        // its late arrival re-ran this map it would replay the stale saved selection over the user's picks.
        const baseItems$ = combineLatest([
            workflows$,
            serviceSteps$,
            this.workflowValue$
        ]).pipe(
            map(([workflows, workflowSteps, value]) => {
                const allItems = [...workflows, ...workflowSteps];

                if (value) {
                    const selectedItems = allItems.filter(item => {
                        return value.workflows.includes(item.entityId) ||
                            value.workflowSteps.includes(item.entityId);
                    });

                    if (selectedItems.length > 0) {
                        this.control.setValue(selectedItems, { emitEvent: false });
                    }
                }

                return allItems;
            })
        );

        this.workflowOptions$ = combineLatest([baseItems$, this.robertWorkflowItems$]).pipe(
            map(([allItems, robertWorkflowItems]) => [...allItems, ...robertWorkflowItems])
        );
    }

    mapToWorkflowItem(item: DropDownItem): WorkflowDropdownItem {
        return {
            ...item,
            id: Math.random().toString(16).slice(2),
            entityId: item.id,
            entity: item.useAsDefaultFilter ? WorkflowDropdownItemEntity.Workflow : WorkflowDropdownItemEntity.WorkflowStep
        };
    }

    // Stamps/clears the Robert marker (via the tp-dropdown per-option cssStyles hook) on the rendered step
    // options, in place. Workflow (group) options never get a marker — only concrete steps go to Robert.
    private applyRobertMarkers(): void {
        if (!this.mappedItems.length) return;
        const robertIds = new Set(this.robertServiceIds ?? []);
        for (const item of this.mappedItems) {
            if (item.entity !== WorkflowDropdownItemEntity.WorkflowStep) continue;
            item.cssStyles = robertIds.has(item.entityId) ? ROBERT_OPTION_STYLE : undefined;
        }
    }

    // The "Robert workflows" section rows. Grayed/unclickable (pointer-events:none) when the project is
    // locked to a DIFFERENT frozen workflow, or when a Robert step has no mapped TP service — with the reason
    // appended so the PM/admin knows why. The frozen flow itself stays clickable and is tagged as current.
    private buildRobertWorkflowItems(): WorkflowDropdownItem[] {
        const frozenName = (this.robertWorkflows ?? []).find(w => w.isProjectWorkflow)?.name;
        return (this.robertWorkflows ?? []).map(wf => {
            const unmapped = wf.unmappedStepIds ?? [];
            const disabled = !!wf.lockedByProject || unmapped.length > 0 || !(wf.mappedServiceIds?.length);
            let name = wf.name;
            if (wf.lockedByProject) {
                name = `${wf.name} (project uses ${frozenName ?? 'its own Robert workflow'})`;
            } else if (unmapped.length > 0) {
                name = `${wf.name} (${unmapped.map(robertStepName).join(', ')} not mapped in TP)`;
            } else if (wf.isProjectWorkflow) {
                name = `${wf.name} (this project's workflow)`;
            }
            return {
                id: Math.random().toString(16).slice(2),
                entityId: wf.id,
                entity: WorkflowDropdownItemEntity.RobertWorkflow,
                name,
                robertMappedServiceIds: wf.mappedServiceIds ?? [],
                robertDisabled: disabled,
                cssStyles: disabled ? ROBERT_OPTION_STYLE_DISABLED : ROBERT_OPTION_STYLE
            };
        });
    }

    // A "Robert workflows" row is a PRESET, not a saved value (getWorkflowValue() ignores it): checking it
    // swaps the selection to the row ITSELF + its mapped TP steps (in the Robert workflow's step order, so the
    // setup matcher sees the exact sequence); unchecking it deselects its mapped steps. mat-select has already
    // toggled the row in control.value by the time this runs — the row's presence tells the direction.
    private applyRobertWorkflowPreset(e: WorkflowDropdownItem): void {
        const current: WorkflowDropdownItem[] = this.control.value ?? [];
        const nowSelected = current.some(item => item.id === e.id);
        if (e.robertDisabled) {
            // pointer-events:none should prevent reaching here (e.g. keyboard) — drop the row defensively.
            if (nowSelected) this.control.setValue(current.filter(item => item.id !== e.id), { emitEvent: false });
            return;
        }
        if (!nowSelected) {
            // Just unchecked — the mapped steps leave with it.
            const mapped = new Set(e.robertMappedServiceIds ?? []);
            this.control.setValue(current.filter(item =>
                !(item.entity === WorkflowDropdownItemEntity.WorkflowStep && mapped.has(item.entityId))
            ));
            return;
        }
        const stepByServiceId = new Map(
            this.mappedItems
                .filter(item => item.entity === WorkflowDropdownItemEntity.WorkflowStep)
                .map(item => [item.entityId, item])
        );
        // Services absent from this job type's step options simply don't get selected — the Robert checklist
        // surfaces the resulting non-match.
        const steps = (e.robertMappedServiceIds ?? [])
            .map(serviceId => stepByServiceId.get(serviceId))
            .filter(item => !!item);
        this.control.setValue([e, ...steps]);
    }

    // A checked Robert-workflow row claims "these exact steps are selected" — once the user manually unchecks
    // one of its steps (or a workflow pick trims the set), the claim is stale, so the row unchecks itself.
    private uncheckStaleRobertWorkflows(): void {
        const current: WorkflowDropdownItem[] = this.control.value ?? [];
        const selectedStepIds = new Set(
            current.filter(item => item.entity === WorkflowDropdownItemEntity.WorkflowStep).map(item => item.entityId)
        );
        const cleaned = current.filter(item =>
            item.entity !== WorkflowDropdownItemEntity.RobertWorkflow ||
            (item.robertMappedServiceIds ?? []).every(serviceId => selectedStepIds.has(serviceId))
        );
        if (cleaned.length !== current.length) this.control.setValue(cleaned, { emitEvent: false });
    }

    onOption(e: WorkflowDropdownItem) {
        if (e.entity === WorkflowDropdownItemEntity.RobertWorkflow) {
            this.applyRobertWorkflowPreset(e);
            return;
        }
        const workflows: WorkflowDropdownItem[] = this.control.value;
        if (e.entity === WorkflowDropdownItemEntity.Workflow) {
            if (workflows?.length > 0) {
                this.control.setValue(
                    workflows.filter(
                        workflow => workflow.entity === WorkflowDropdownItemEntity.WorkflowStep || workflow.id === e.id
                    ),
                    { emitEvent: false }
                );
            }
        }
        this.uncheckStaleRobertWorkflows();
    }

    getWorkflowValue(): WorkflowDropdownValue {
        const workflowItems: WorkflowDropdownItem[] = this.control.value ?? [];
        const workflowSteps = workflowItems.filter(
            workflowItem => workflowItem.entity === WorkflowDropdownItemEntity.WorkflowStep
        ).map(
            workflowItem => workflowItem.entityId
        );
        const workflows = workflowItems.filter(
            workflowItem => workflowItem.entity === WorkflowDropdownItemEntity.Workflow
        ).map(
            workflowItem => workflowItem.entityId
        );
        return { workflows, workflowSteps };
    }
}
